// §6.1 — the remaining Windows chrome: notifications, the tray flyout, and the two screens.
//
// §6.3 puts Windows notifications "bottom-right toast → tray flyout": the toast appears above the
// taskbar's right corner, and what has been said lives on in the flyout the tray opens. §13's
// model — the Center, grouping, actions, do-not-disturb — arrived at M10.1 and is one component
// every skin mounts (D-140); the flyout is this skin's *shortcut into* it, listing what is in the
// Center alongside the shell's own one-shot toast. `LoginScreen` gets its content at M12.9.
import { useEffect } from 'react';
import { X } from '@/assets/icons';
import { COPY } from '@/copy';
import { beginSignIn } from '@/kernel/server';
import { NotificationItem } from '@/skins/notifications';
import { TrayCalendar } from '@/skins/tray-calendar';
import { useKernel, useNotifications, useStoreValue } from '@/kernel/react';

/**
 * §6.3 — bottom-right, above the taskbar.
 *
 * Two sources, one place: §13's notifications, whose 6 s life the kernel owns, and `shellui`'s
 * single shell-authored toast (§6.4's stolen-chord message, a skipped session), which belongs to
 * no app and never reaches the Center.
 */
export function NotificationHost() {
  const kernel = useKernel();
  const toast = useStoreValue(kernel.shellui.store, (s) => s.toast);
  const { toasts } = useNotifications();

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => kernel.shellui.dismissToast(), 6_000);
    return () => clearTimeout(timer);
  }, [toast, kernel]);

  if (!toast && toasts.length === 0) return null;
  return (
    <div className="win11-toasts" data-testid="notification-host">
      {toasts.map((item) => (
        <div
          key={item.id}
          className="win11-toast"
          role="status"
          data-testid={`notification-toast-${item.id}`}
          data-app-id={item.appId}
        >
          {/* §13 — clicking the body focuses the window that sent it. */}
          <button
            type="button"
            className="win11-toast-body"
            data-testid={`notification-toast-open-${item.id}`}
            onClick={() => kernel.notify.activate(item.id)}
          >
            <span className="win11-toast-title">{item.title}</span>
            {item.body !== undefined && item.body.length > 0 && (
              <span className="win11-toast-text">{item.body}</span>
            )}
          </button>
          {item.actions.map((action) => (
            <button
              key={action}
              type="button"
              className="win11-toast-action"
              data-testid={`notification-toast-action-${item.id}-${action}`}
              onClick={() => kernel.notify.act(item.id, action)}
            >
              {action}
            </button>
          ))}
          <button
            type="button"
            className="win11-toast-dismiss"
            aria-label={COPY.chrome.dismiss}
            onClick={() => kernel.notify.dismissToast(item.id)}
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      ))}
      {toast && (
        <div className="win11-toast" role="status" data-testid="shell-toast">
          <span data-testid="shell-toast-message">{toast.message}</span>
          <button
            type="button"
            className="win11-toast-dismiss"
            aria-label={COPY.chrome.dismiss}
            onClick={() => kernel.shellui.dismissToast()}
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * §6.3 / §11.10 — what the tray opens: the clock and this month (§11.10's "tray flyout"), then
 * what is in the Center and what the shell last said.
 *
 * This is the one skin whose tray is a *separate surface* from its Center, which is why §11.10's
 * flyout lands here: on the house skin the bar opens the Center itself, and on macOS the Center
 * **is** the side panel (D-171). Both reach the same month through the app, one click away.
 */
export function TrayFlyout() {
  const kernel = useKernel();
  const toast = useStoreValue(kernel.shellui.store, (s) => s.toast);
  const { center } = useNotifications();
  const empty = center.length === 0 && !toast;

  return (
    <div className="win11-tray-flyout" data-testid="tray-flyout" role="dialog" aria-label={COPY.chrome.notifications}>
      <TrayCalendar />
      {toast && (
        <p className="win11-tray-item" data-testid="tray-flyout-item">
          {toast.message}
        </p>
      )}
      {center.length > 0 && (
        <ul className="win11-tray-list" data-testid="tray-flyout-list">
          {center.map((item) => (
            <NotificationItem key={item.id} item={item} />
          ))}
        </ul>
      )}
      {empty && (
        <p className="win11-tray-empty" data-testid="tray-flyout-empty">
          {COPY.chrome.noNotifications}
        </p>
      )}
    </div>
  );
}

/** §8.3 — the whole page when an admin has turned anonymous access off (M12.9). */
export function LoginScreen() {
  return (
    <div className="win11-login" data-testid="login-screen">
      <div className="win11-login-panel">
        <h1 className="win11-login-title">{COPY.loginTitle}</h1>
        <p className="win11-login-body">{COPY.loginBody}</p>
        <button
          type="button"
          className="win11-login-action"
          data-testid="login-signin"
          onClick={() => beginSignIn(() => undefined)}
        >
          {COPY.loginAction}
        </button>
      </div>
    </div>
  );
}

/** §6.4 — `Meta+L`. A click anywhere unlocks; M12.9 puts a real sign-in behind it. */
export function LockScreen() {
  const kernel = useKernel();
  const locked = useStoreValue(kernel.shellui.store, (s) => s.overlay === 'lock');
  if (!locked) return null;
  return (
    <div
      className="win11-lock"
      data-testid="lock-screen"
      onClick={() => kernel.shellui.closeOverlay()}
    >
      <div className="win11-lock-clock" data-testid="lock-clock">
        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}
